import React, { useState } from 'react';
import { TableCell } from 'components/mui';
import { format } from 'date-fns';
import { TextField, SxProps, Theme } from '@mui/material';
import useSaveComment from 'containers/pages/comments/hooks/useSaveComment';
import { CANtrackComment } from 'containers/pages/comments/types/comment';

/**
 * Editable cell for time entry. Used in the CommentTableRow component.
 * @param {CANtrackComment} comment - The comment object.
 * @param {React.Dispatch<React.SetStateAction<CANtrackComment[]>>} setComments - The state setter for the comments array.
 * @returns
 */
export const TimeEntryEditableCell: React.FC<{
	comment: CANtrackComment;
	setComments: React.Dispatch<React.SetStateAction<CANtrackComment[]>>;
	showDate?: boolean;
	sx?: SxProps<Theme>;
}> = ({ comment, setComments, showDate, sx }) => {
	const [isEditing, setIsEditing] = useState<boolean>(false);
	const [currentValue, setCurrentValue] = useState<Date>(comment.date);
	const { saveComment } = useSaveComment(setComments);

	const handleBlur = () => {
		if (currentValue !== comment.date) {
			saveComment(
				{
					...comment,
					date: currentValue,
				}, // update the date property of the comment object
				() => setIsEditing(false), // callback to set isEditing to false
			);
		} else {
			setIsEditing(false);
		}
	};

	const handleKeyPress = (e: any) => {
		if (e.key === 'Enter') {
			handleBlur();
		}
		if (e.key === 'Escape') {
			setCurrentValue(comment.date);
			setIsEditing(false);
		}
	};

	/**
	 * Handles the change event for the time input field.
	 * Updates the current value state with the new time. Note that the date portion of the current value is not changed.
	 *
	 * @param {React.ChangeEvent<HTMLInputElement>} e - The change event from the time input field.
	 */
	const handleTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const newTime = e.target.value;
		if (currentValue) {
			const [hours, minutes] = newTime.split(':');
			const updatedDate = new Date(currentValue);
			updatedDate.setHours(parseInt(hours, 10));
			updatedDate.setMinutes(parseInt(minutes, 10));
			setCurrentValue(updatedDate);
		}
	};

	return (
		<TableCell
			align="left"
			onClick={() => setIsEditing(true)}
			sx={{ width: showDate ? '160px' : '100px', ...sx }}
		>
			{isEditing ? (
				<TextField
					autoFocus
					fullWidth
					type="time"
					value={format(currentValue ?? comment.date, 'HH:mm')}
					onChange={handleTimeChange}
					onBlur={handleBlur}
					onKeyUp={handleKeyPress}
					InputLabelProps={{
						shrink: true,
					}}
				/>
			) : (
				<span>
					{showDate && <>{format(comment.date, 'MMM d, yyyy')}{' '}</>}
					{format(comment.date, 'h:mm')}
					&nbsp;
					{format(comment.date, 'aaa')}
				</span>
			)}
		</TableCell>
	);
};
